import { FormEvent, useEffect, useState } from 'react'
import { authClient } from '../../../lib/auth-client'
import { authorizeUrl, getOAuthQuery } from '../../../lib/oauth'
import { toast } from 'react-toastify'
import { useNavigate, Link } from 'react-router-dom'

export const LoginRoute = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const { data: session } = authClient.useSession()
    // Present when an MCP client (via the API's OAuth server) sent the user here to sign in.
    // Always show the form then: the client may have asked to force a fresh login.
    const oauthQuery = getOAuthQuery()

    useEffect(() => {
        if (session?.user && !oauthQuery) {
            navigate('/')
        }
    }, [session, navigate, oauthQuery])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)

        const { data, error } = await authClient.signIn.email({
            email,
            password,
        })

        if (error) {
            toast.error(error.message || 'Invalid credentials')
            setLoading(false)
            return
        }

        if (data) {
            window.location.href = oauthQuery ? authorizeUrl(oauthQuery) : '/'
        }

        setLoading(false)
    }

    return (
        <div className="flex min-h-screen">
            <div className="hidden lg:flex lg:w-1/2 bg-foreground items-center justify-center p-12">
                <div className="max-w-md">
                    <h2 className="text-3xl font-semibold tracking-tight text-background">
                        Trakkrr
                    </h2>
                    <p className="text-background/60 mt-3 text-sm leading-relaxed">
                        Monitor your fleet, manage devices, and keep operations
                        running with real-time insights.
                    </p>
                </div>
            </div>

            <div className="flex flex-1 flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 bg-background">
                <div className="w-full max-w-sm mx-auto">
                    <div className="mb-8">
                        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                            Sign in
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1.5">
                            {oauthQuery
                                ? 'Sign in to connect an application to your Trakkrr account.'
                                : 'Enter your credentials to access your account.'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label
                                htmlFor="email"
                                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label
                                htmlFor="password"
                                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                            >
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                placeholder="Enter your password"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="h-10 w-full rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/forgot-password"
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                            Forgot your password?
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}
